import { useModalStore } from '@/shared/lib';
import { NUMERIC_CONFIG } from '@/shared/config';
import { useTranslation } from 'react-i18next';
import {
  ResultModal,
  WithdrawCardModal,
  type WithdrawCardModalState,
  WithdrawConfirmationModalContainer,
} from '@/features/withdraw-card';
// import { WithdrawHistoryModal } from '@/shared/components';
// import type { OperationHistoryParams } from '@/shared/types';
import { getCalculatedCommission } from '@/entities/operation';
import { useCreateSavedAddressModal } from '@/widgets/create-saved-address-modal';
import { useEditCardModal } from '@/features/edit-card';

export function useWithdrawCardModals() {
  const { showDialogComponent, closeDialog, getState, cleanup, getAllState } =
    useModalStore();
  const { t } = useTranslation();

  const openOperationMethodsModal = getState(
    'onWithdrawFormBack'
  ) as () => void;
  // TODO think about following FSD pattern
  const { openCreateSavedAddressModal } = useCreateSavedAddressModal();
  const { openEditCardModal } = useEditCardModal();

  const onCloseDialog = () => {
    closeDialog();
    cleanup();
  };

  const closeConfirmation = {
    title: t('withdrawMethods.crypto.exitConfirmTitle'),
    description: t('withdrawMethods.crypto.exitConfirmDescription'),
    confirmText: t('withdrawMethods.crypto.exitConfirmButton'),
    showCancelButton: false,
  };

  function getStoreData() {
    const storeData = getAllState<WithdrawCardModalState>();

    const commissionData = getCalculatedCommission({
      amount: +storeData?.totalAmount || 0,
      assetCode: storeData?.withdrawCardFormData?.currencyCode || '',
      includeFee: !!storeData?.withdrawCardFormData?.includeFee,
      fee: storeData?.fee || 0,
      precision: NUMERIC_CONFIG.precision.default,
    });

    return {
      commissionData,
      storeData,
    };
  }

  const openResultModal = ({ success }: { success: boolean }) => {
    const { commissionData, storeData } = getStoreData();

    showDialogComponent({
      component: (
        <ResultModal
          success={success}
          data={{
            commissionData,
            cardNumber: storeData.withdrawCardFormData.cardNumber,
          }}
          isNeedToSaveTemplate={storeData.isNeedToSaveTemplate}
          onClose={() => {
            if (success) {
              onCloseDialog();
              return;
            }

            openWithdrawCardModal();
          }}
          onSaveAddress={() =>
            openCreateSavedAddressModal({
              onBack: openWithdrawCardModal,
              onCreateSuccess: openOperationMethodsModal,
              showOnlyTab: 'card',
              initialCardFormValues: {
                currencyCode: storeData.withdrawCardFormData.currencyCode,
                cardNumber: storeData.withdrawCardFormData.cardNumber,
                isCardholderCard:
                  storeData.withdrawCardFormData.isCardholderCard,
                cardholderName: storeData.withdrawCardFormData.cardholderName,
              },
            })
          }
        />
      ),
      onClose: onCloseDialog,
    });
  };

  // const openWithdrawHistory = (historyParams?: OperationHistoryParams) => {
  //   showDialogComponent({
  //     component: (
  //       <WithdrawHistoryModal
  //         onBack={openWithdrawCardModal}
  //         title={t('withdrawMethods.crypto.history.dialogTitle')}
  //         historyParams={historyParams}
  //       />
  //     ),
  //     onClose: onCloseDialog,
  //     closeConfirmation,
  //     skipBackButtonConfirmation: true,
  //     closeConfirmationRequired: true,
  //   });
  // };

  const openWithdrawCardModal = (assetCode?: string) => {
    const openOperationMethodsModal = getState(
      'onWithdrawFormBack'
    ) as () => void;

    const goBack = () => {
      cleanup();
      openOperationMethodsModal();
    };

    showDialogComponent({
      component: (
        <WithdrawCardModal
          // Withdraw history is temporarily disabled
          // onHistoryClick={openWithdrawHistory}
          assetCode={assetCode}
          title={t('withdrawMethods.card.title')}
          onBack={goBack}
          onEditSavedCard={savedCard =>
            openEditCardModal({ savedCard, onBack: openWithdrawCardModal })
          }
        />
      ),
      onClose: onCloseDialog,
      preventOverlayClose: true,
      closeConfirmation,
      skipBackButtonConfirmation: false,
    });
  };

  const openConfirmationModal = (params?: { continueMFA?: boolean }) => {
    const { commissionData, storeData } = getStoreData();

    showDialogComponent({
      component: (
        <WithdrawConfirmationModalContainer
          continueMFA={params?.continueMFA}
          onBack={openWithdrawCardModal}
          storeData={storeData}
          commissionData={commissionData}
        />
      ),
      onClose: onCloseDialog,
      preventOverlayClose: true,
      closeConfirmation,
      skipBackButtonConfirmation: true,
      closeConfirmationRequired: true,
    });
  };

  return {
    openResultModal,
    openWithdrawCardModal,
    openConfirmationModal,
  };
}
